import { useRef, useState } from "react";
import { Alert, Checkbox, Radio, Space, Tag } from "antd";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import Button from "shared/components/Button";
import ScreenThumbnail from "./ScreenThumbnail";
import { conditionSummary } from "./catalog";

/**
 * A model rewriting a screen instead of editing it produces markup that looks
 * right and no longer works: the elements are there, their bindings are not.
 * Losing more than a third of the slots is the signal, and it is worth showing
 * even when the operator asked for a change that legitimately removes a few.
 */
const SLOT_DROP_RATIO = 0.3;

export const hasSlotCollapse = (proposal) => {
  const before = get(proposal, "slotsBefore");
  const after = get(proposal, "slotsAfter");
  if (isNil(before) || isNil(after) || before === 0) return false;
  return after < before * (1 - SLOT_DROP_RATIO);
};

const BEFORE = "before";
const AFTER = "after";

// Below this, a press reads as a glance rather than a decision: the button
// flips back on release. Longer, and the operator meant to stay on the old
// version to study it, so the click sticks.
const HOLD_MS = 250;

/**
 * Shows what a model produced and waits for a decision, so nothing replaces a
 * working screen before someone has looked at it. It knows nothing of the three
 * paths that feed it — generation, state generation, correction: each supplies
 * its own proposals and its own way to apply them.
 */
const ScreenProposalPreview = ({
  proposals,
  styleContent,
  labels,
  applying,
  onApply,
  onCancel,
}) => {
  const [selected, setSelected] = useState(0);
  // Everything produced is kept by default; unticking is how one applies the
  // states of a board without replacing the body that already works.
  const [excluded, setExcluded] = useState([]);
  // Toggling one frame beats two thumbnails side by side: at this size a
  // missing header only shows when the two images swap in place.
  const [side, setSide] = useState(AFTER);
  const pressedAt = useRef(0);
  // What was on screen when the press started, and whether a press is still
  // in progress: without the first, a short click undoes the flip the press
  // itself made; without the second, moving the mouse away cancels a flip the
  // operator deliberately parked.
  const sideAtPress = useRef(AFTER);
  const holding = useRef(false);

  if (!proposals || !proposals.length) return null;

  const proposal = proposals[Math.min(selected, proposals.length - 1)];
  const previous = get(proposal, "previousHtmlContent");
  const showingBefore = side === BEFORE && previous;
  const showing = showingBefore ? previous : proposal.htmlContent;
  const keyOf = (item, index) => item.targetId || `${index}`;
  const kept = proposals.filter(
    (item, index) => excluded.indexOf(keyOf(item, index)) < 0,
  );
  const collapsed = kept.filter(hasSlotCollapse);

  // A state carries no stylesheet of its own. When the batch also proposes a
  // new one — a board generating a screen and its variants — that sheet is
  // not stored anywhere yet, so the states would preview unstyled and look
  // broken while being perfectly correct.
  const batchStyle = get(
    proposals.find((item) => item.styleContent),
    "styleContent",
    null,
  );

  // A state is applied on the strength of two things: the markup, which is on
  // screen, and the condition, which is not. When the model picked that
  // condition itself, this line is the only place it can be read before it is
  // written, and its absence is worth more than a missing label: a state
  // without a condition renders perfectly and never appears.
  const trigger = conditionSummary(proposal.conditions);
  const missingTrigger = proposal.kind === "state" && !trigger;

  const slotSummary = (item) => {
    if (isNil(get(item, "slotsAfter"))) return null;
    if (isNil(get(item, "slotsBefore")))
      return `${item.slotsAfter} ${labels.SLOT_COUNT}`;
    return `${item.slotsBefore} → ${item.slotsAfter} ${labels.SLOT_COUNT}`;
  };

  return (
    <div className="canvas-editor__proposal">
      <div className="canvas-editor__section-title">{labels.TITLE}</div>

      {collapsed.length ? (
        <Alert
          type="warning"
          showIcon
          className="mb-12"
          message={labels.COLLAPSE_WARNING}
          description={collapsed
            .map(
              (item) =>
                `${item.targetName || labels.TARGET_VERSION} : ${slotSummary(item)}`,
            )
            .join(" · ")}
        />
      ) : null}

      {proposals.length > 1 ? (
        <Radio.Group
          className="mb-12"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          {proposals.map((item, index) => (
            <Radio.Button key={item.targetId || index} value={index}>
              {item.targetName || labels.TARGET_VERSION}
              {excluded.indexOf(keyOf(item, index)) >= 0
                ? " —"
                : hasSlotCollapse(item)
                  ? " ⚠"
                  : ""}
            </Radio.Button>
          ))}
        </Radio.Group>
      ) : null}

      <div className="canvas-editor__row-between mb-8">
        <Space size={8}>
          <Checkbox
            checked={excluded.indexOf(keyOf(proposal, selected)) < 0}
            disabled={applying}
            onChange={(e) => {
              const key = keyOf(proposal, selected);
              setExcluded((current) =>
                e.target.checked
                  ? current.filter((item) => item !== key)
                  : [...current, key],
              );
            }}
          >
            {proposal.targetName || labels.TARGET_VERSION}
          </Checkbox>
          {slotSummary(proposal) ? (
            <Tag color={hasSlotCollapse(proposal) ? "warning" : "default"}>
              {slotSummary(proposal)}
            </Tag>
          ) : null}
          {trigger ? (
            <Tag color="processing">{`${labels.TRIGGER} ${trigger}`}</Tag>
          ) : null}
          {missingTrigger ? <Tag color="error">{labels.NO_TRIGGER}</Tag> : null}
        </Space>
        {previous ? (
          <button
            type="button"
            className={`screen-workshop__compare${
              side === BEFORE ? " screen-workshop__compare--on" : ""
            }`}
            title={labels.COMPARE_HINT}
            // Held down it is a glance, clicked it is a stay. The same button
            // carries both because they are the same question asked at two
            // speeds, and a second control would only say so twice.
            onMouseDown={() => {
              pressedAt.current = Date.now();
              sideAtPress.current = side;
              holding.current = true;
              setSide(BEFORE);
            }}
            onMouseUp={() => {
              holding.current = false;
              if (Date.now() - pressedAt.current >= HOLD_MS) setSide(AFTER);
            }}
            onMouseLeave={() => {
              if (!holding.current) return;
              holding.current = false;
              setSide(AFTER);
            }}
            onClick={() => {
              if (Date.now() - pressedAt.current >= HOLD_MS) return;
              // The press already switched to the previous version, so a
              // short click only has to decide whether to undo it.
              if (sideAtPress.current === BEFORE) setSide(AFTER);
            }}
          >
            {side === BEFORE ? labels.SIDE_BEFORE : labels.COMPARE}
          </button>
        ) : null}
      </div>

      <ScreenThumbnail
        html={showing || ""}
        styleContent={
          showingBefore
            ? get(proposal, "previousStyleContent") || styleContent
            : proposal.styleContent || batchStyle || styleContent
        }
        size="lg"
        scrollable
        title={proposal.targetName || labels.TARGET_VERSION}
      />

      <div className="canvas-editor__row mt-12">
        <Button
          type="primary"
          loading={applying}
          disabled={!kept.length}
          onClick={() => onApply(kept)}
        >
          {`${labels.APPLY} (${kept.length})`}
        </Button>
        <Button type="gray--outlined" disabled={applying} onClick={onCancel}>
          {labels.CANCEL}
        </Button>
      </div>
      <div className="canvas-editor__meta">{labels.HINT}</div>
    </div>
  );
};

ScreenProposalPreview.propTypes = {
  proposals: PropTypes.array,
  styleContent: PropTypes.string,
  labels: PropTypes.object,
  applying: PropTypes.bool,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
};

ScreenProposalPreview.defaultProps = {
  proposals: [],
  styleContent: undefined,
  labels: {},
  applying: false,
  onApply: undefined,
  onCancel: undefined,
};

export default ScreenProposalPreview;
